import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  Squares2X2Icon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import MapboxMiniMap from '../maps/MapboxMiniMap';
import {
  fetchGridPreview,
  generateTrees,
  clearFieldTrees,
  fetchGridStatus,
} from '../../services/traceabilityApi';
import type {
  FieldPlot,
  GridPreview,
  GridPreviewPoint,
} from '../../services/traceabilityApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BuilderStep = 'field' | 'configure' | 'preview' | 'confirm' | 'result';

interface Props {
  field: FieldPlot;
  onClose: () => void;
  onComplete: () => void;
}

interface PreviewResult {
  preview: GridPreview;
  requested_count: number;
  existing_tree_count: number;
  field_code: string;
  field_name: string;
}

interface GenerateResult {
  created_count: number;
  requested_count: number;
  mapped_count: number;
  inside_boundary_count: number;
  invalid_count: number;
  total_after: number;
  existing_before: number;
  grid: any;
  validation: any;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const parseBoundary = (value: any): Array<{ lat: number; lng: number }> => {
  if (!value) return [];
  let raw = value;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { return []; }
  }
  if (!Array.isArray(raw)) return [];
  const points = raw
    .map((p: any) =>
      Array.isArray(p)
        ? { lat: Number(p[0]), lng: Number(p[1]) }
        : { lat: Number(p.lat ?? p.latitude), lng: Number(p.lng ?? p.longitude) }
    )
    .filter((p: any) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (points.length > 3) {
    const first = points[0];
    const last = points[points.length - 1];
    if (Math.abs(first.lat - last.lat) < 1e-7 && Math.abs(first.lng - last.lng) < 1e-7) {
      points.pop();
    }
  }
  return points;
};

const fmt = (v: number | string | undefined) =>
  new Intl.NumberFormat('en-US').format(Number(v || 0));

// ---------------------------------------------------------------------------
// Step 1: Field Summary
// ---------------------------------------------------------------------------

const FieldStep: React.FC<{ field: FieldPlot; boundary: Array<{ lat: number; lng: number }> }> = ({
  field,
  boundary,
}) => (
  <div className="space-y-4">
    <div className="rounded-lg bg-slate-50 p-4 border border-slate-100">
      <p className="text-sm font-bold text-slate-900">{field.name || field.plot_code}</p>
      <p className="text-xs font-mono text-slate-500 mt-0.5">{field.plot_code}</p>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {[
        ['Crop', field.crop_type],
        ['Area', field.area_hectares ? `${field.area_hectares} ha` : '—'],
        ['Farmers', field.farmer_name || 'Not assigned'],
        ['Agronomist', field.assigned_agronomist_name || 'Not assigned'],
        ['Existing trees', fmt(field.registered_tree_count)],
        ['Expected', fmt(field.expected_tree_count)],
      ].map(([label, value]) => (
        <div key={label} className="bg-white rounded-lg border border-slate-100 p-3">
          <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">{label}</p>
          <p className="text-sm font-semibold text-slate-800 mt-0.5">{String(value)}</p>
        </div>
      ))}
    </div>
    {boundary.length >= 3 ? (
      <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 flex items-start gap-2">
        <CheckCircleIcon className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
        <p className="text-xs text-emerald-700">
          Boundary is stored with {boundary.length} vertices. Ready to generate a tree grid.
        </p>
      </div>
    ) : (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
        <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-700">
          No boundary is saved for this field. Open Farm Maps to draw the boundary before generating trees.
        </p>
      </div>
    )}
    {boundary.length >= 3 && (
      <div className="h-48 rounded-xl overflow-hidden border border-slate-200">
        <MapboxMiniMap
          center={[boundary[0].lat, boundary[0].lng]}
          zoom={15}
          fitBounds={true}
          polygons={[{ id: 'field-boundary', coordinates: boundary.map(p => [p.lat, p.lng] as [number, number]), color: '#FACC15', fillColor: '#FACC15', fillOpacity: 0.15 }]}
        />
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Step 2: Grid Configuration
// ---------------------------------------------------------------------------

const ConfigureStep: React.FC<{
  expectedCount: number;
  setExpectedCount: (n: number) => void;
  rowSpacing: number;
  setRowSpacing: (n: number) => void;
  treeSpacing: number;
  setTreeSpacing: (n: number) => void;
  bearing: number;
  setBearing: (n: number) => void;
  mode: 'auto' | 'manual';
  setMode: (m: 'auto' | 'manual') => void;
  field: FieldPlot;
}> = ({
  expectedCount,
  setExpectedCount,
  rowSpacing,
  setRowSpacing,
  treeSpacing,
  setTreeSpacing,
  bearing,
  setBearing,
  mode,
  setMode,
  field,
}) => (
  <div className="space-y-5">
    <div>
      <p className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Placement mode</p>
      <div className="flex rounded-lg border border-slate-200 overflow-hidden">
        {(['auto', 'manual'] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`flex-1 py-2.5 text-xs font-bold capitalize transition ${
              mode === m ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {m === 'auto' ? 'Automatic' : 'Manual'}
          </button>
        ))}
      </div>
    </div>

    <div>
      <label className="block">
        <span className="text-xs font-bold uppercase text-slate-500">Expected tree count</span>
        <input
          type="number"
          min={1}
          max={5000}
          value={expectedCount}
          onChange={(e) => setExpectedCount(Number(e.target.value) || 0)}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
        />
      </label>
      {field.expected_tree_count ? (
        <p className="mt-1 text-[11px] text-slate-400">
          Field configured for {fmt(field.expected_tree_count)} expected trees
        </p>
      ) : null}
    </div>

    <div className="grid grid-cols-2 gap-3">
      <label className="block">
        <span className="text-xs font-bold uppercase text-slate-500">Row spacing (m)</span>
        <input
          type="number"
          min={2}
          max={50}
          step={0.5}
          value={rowSpacing}
          onChange={(e) => setRowSpacing(Number(e.target.value) || 6)}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
        />
      </label>
      <label className="block">
        <span className="text-xs font-bold uppercase text-slate-500">Tree spacing (m)</span>
        <input
          type="number"
          min={2}
          max={50}
          step={0.5}
          value={treeSpacing}
          onChange={(e) => setTreeSpacing(Number(e.target.value) || 6)}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
        />
      </label>
    </div>

    {mode === 'manual' && (
      <label className="block">
        <span className="text-xs font-bold uppercase text-slate-500">Grid bearing (degrees from north)</span>
        <input
          type="number"
          min={0}
          max={360}
          step={1}
          value={bearing}
          onChange={(e) => setBearing(Number(e.target.value) || 0)}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
        />
      </label>
    )}

    <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
      <p className="text-[11px] text-slate-500 leading-5">
        {mode === 'auto'
          ? 'The system will calculate the optimal row orientation from the longest field edge and place trees at the configured spacing inside the boundary.'
          : 'You control the bearing angle. The system will sweep parallel rows at your configured spacing.'}
      </p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Step 3: Preview Map
// ---------------------------------------------------------------------------

const PreviewStep: React.FC<{
  preview: PreviewResult;
  boundary: Array<{ lat: number; lng: number }>;
}> = ({ preview, boundary }) => {
  const { preview: data } = preview;
  const points = data.points;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        {[
          ['Requested', fmt(preview.requested_count)],
          ['Proposed', fmt(data.validation.proposed)],
          ['Inside', `${fmt(data.validation.inside_boundary)} ✓`],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-center">
            <p className="text-lg font-black text-slate-900">{value}</p>
            <p className="text-[10px] font-bold uppercase text-slate-400">{label}</p>
          </div>
        ))}
      </div>

      <div className="h-80 rounded-xl overflow-hidden border border-slate-200 relative">
        <MapboxMiniMap
          center={[boundary[0].lat, boundary[0].lng]}
          zoom={15}
          scrollWheelZoom={true}
          fitBounds={true}
          polygons={[{ id: 'field-boundary', coordinates: boundary.map(p => [p.lat, p.lng] as [number, number]), color: '#FACC15', fillColor: '#FACC15', fillOpacity: 0.1 }]}
          markers={points.map((pt: GridPreviewPoint, idx: number) => ({
            id: idx,
            position: [pt.latitude, pt.longitude] as [number, number],
            color: '#16A34A',
          }))}
        />
        <div className="absolute bottom-2 left-2 bg-white/90 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-600 shadow-sm">
          {points.length} tree positions
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-slate-50 rounded-lg p-2">
          <span className="text-slate-400">Rows:</span>{' '}
          <span className="font-bold text-slate-700">{data.grid.rows}</span>
        </div>
        <div className="bg-slate-50 rounded-lg p-2">
          <span className="text-slate-400">Cols:</span>{' '}
          <span className="font-bold text-slate-700">{data.grid.columns}</span>
        </div>
        <div className="bg-slate-50 rounded-lg p-2">
          <span className="text-slate-400">Row spacing:</span>{' '}
          <span className="font-bold text-slate-700">{data.grid.row_spacing_m}m</span>
        </div>
        <div className="bg-slate-50 rounded-lg p-2">
          <span className="text-slate-400">Tree spacing:</span>{' '}
          <span className="font-bold text-slate-700">{data.grid.tree_spacing_m}m</span>
        </div>
        <div className="bg-slate-50 rounded-lg p-2">
          <span className="text-slate-400">Bearing:</span>{' '}
          <span className="font-bold text-slate-700">{data.grid.bearing_degrees}°</span>
        </div>
        <div className="bg-slate-50 rounded-lg p-2">
          <span className="text-slate-400">Area:</span>{' '}
          <span className="font-bold text-slate-700">{data.capacity.area_ha} ha</span>
        </div>
      </div>

      {data.capacity.recommended_spacing && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
          {data.capacity.recommended_spacing}
        </div>
      )}

      {data.validation.outside_boundary > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          {data.validation.outside_boundary} positions are outside the boundary.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 4: Confirm
// ---------------------------------------------------------------------------

const ConfirmStep: React.FC<{
  field: FieldPlot;
  preview: PreviewResult;
  loading: boolean;
}> = ({ field, preview, loading }) => {
  const { preview: data } = preview;
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <Squares2X2Icon className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-black text-slate-900">Generate tree grid</p>
            <p className="text-xs text-slate-500">inside {field.name || field.plot_code}</p>
          </div>
        </div>

        {preview.existing_tree_count > 0 ? (
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-slate-500">Expected total</span>
              <span className="font-bold text-slate-900">{fmt(data.validation.proposed)} trees</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Existing</span>
              <span className="font-bold text-amber-600">{fmt(preview.existing_tree_count)} trees</span>
            </div>
            <div className="h-px bg-slate-200 my-1" />
            <div className="flex justify-between">
              <span className="text-slate-700 font-semibold">New trees to create</span>
              <span className="font-black text-emerald-700">{fmt(Math.max(data.validation.proposed - preview.existing_tree_count, 0))}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-700 font-semibold">Final total</span>
              <span className="font-black text-slate-900">{fmt(data.validation.proposed)} trees</span>
            </div>
          </div>
        ) : (
          <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-center">
            <p className="text-lg font-black text-emerald-800">{fmt(data.validation.proposed)}</p>
            <p className="text-xs text-emerald-600">trees to generate</p>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-100 bg-slate-50 p-4 space-y-2 text-xs">
        {[
          ['Boundary valid', data.validation.boundary_valid],
          ['All inside boundary', data.validation.all_inside_boundary],
          ['Duplicate positions', data.validation.duplicate_positions === 0],
          ['Count matches', data.validation.count_complete],
        ].map(([label, ok]) => (
          <div key={String(label)} className="flex items-center justify-between">
            <span className="text-slate-600">{String(label)}</span>
            {ok ? (
              <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
            ) : (
              <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
            )}
          </div>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="h-5 w-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
          <span className="ml-2 text-sm text-slate-500">Generating tree records...</span>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 5: Result
// ---------------------------------------------------------------------------

const ResultStep: React.FC<{
  result: GenerateResult;
  field: FieldPlot;
  onClose: () => void;
  onViewMap: () => void;
}> = ({ result, field, onClose, onViewMap }) => (
  <div className="space-y-4">
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center">
      <CheckCircleIcon className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
      <p className="text-lg font-black text-emerald-900">Tree grid generated</p>
      <p className="text-sm text-emerald-700 mt-1">All records created successfully</p>
    </div>

    <div className="grid grid-cols-2 gap-2">
      {[
        ['Requested', result.requested_count, 'text-slate-900'],
        ['Created', result.created_count, 'text-emerald-700'],
        ['Mapped', result.mapped_count, 'text-emerald-700'],
        ['Inside boundary', result.inside_boundary_count, 'text-emerald-700'],
        ['Failed', result.invalid_count, result.invalid_count > 0 ? 'text-red-600' : 'text-emerald-700'],
        ['Total after', result.total_after, 'text-slate-900'],
      ].map(([label, value, color]) => (
        <div key={label} className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-center">
          <p className={`text-xl font-black ${color}`}>{fmt(value)}</p>
          <p className="text-[10px] font-bold uppercase text-slate-400">{String(label)}</p>
        </div>
      ))}
    </div>

    {result.validation && (
      <div className="rounded-lg border border-slate-100 bg-slate-50 p-4 space-y-2 text-xs">
        {[
          ['Boundary valid', result.validation.boundary_valid],
          ['All inside boundary', result.validation.all_inside_boundary],
          ['Codes unique', result.validation.codes_unique],
          ['Count complete', result.validation.count_complete],
        ].map(([label, ok]) => (
          <div key={String(label)} className="flex items-center justify-between">
            <span className="text-slate-600">{String(label)}</span>
            {ok ? (
              <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
            ) : (
              <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
            )}
          </div>
        ))}
      </div>
    )}

    <div className="flex gap-2">
      <button
        onClick={onViewMap}
        className="flex-1 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition"
      >
        View on map
      </button>
      <button
        onClick={onClose}
        className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 transition"
      >
        Close
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const TreeGridBuilderDrawer: React.FC<Props> = ({ field, onClose, onComplete }) => {
  const [step, setStep] = useState<BuilderStep>('field');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [generateResult, setGenerateResult] = useState<GenerateResult | null>(null);

  // Configuration state
  const [expectedCount, setExpectedCount] = useState(field.expected_tree_count || 100);
  const [rowSpacing, setRowSpacing] = useState(Number(field.row_spacing_m) || 6);
  const [treeSpacing, setTreeSpacing] = useState(Number(field.tree_spacing_m) || 6);
  const [bearing, setBearing] = useState(0);
  const [mode, setMode] = useState<'auto' | 'manual'>('auto');
  const [clearing, setClearing] = useState(false);
  const [clearInfo, setClearInfo] = useState<any>(null);
  const [confirmFieldCode, setConfirmFieldCode] = useState('');

  const boundary = useMemo(() => parseBoundary(field.boundary_coordinates), [field.boundary_coordinates]);
  const hasBoundary = boundary.length >= 3;

  const steps: { key: BuilderStep; label: string; num: number }[] = [
    { key: 'field', label: 'Field', num: 1 },
    { key: 'configure', label: 'Grid', num: 2 },
    { key: 'preview', label: 'Preview', num: 3 },
    { key: 'confirm', label: 'Confirm', num: 4 },
    { key: 'result', label: 'Result', num: 5 },
  ];
  const currentStepIdx = steps.findIndex((s) => s.key === step);

  const loadPreview = useCallback(async () => {
    if (!hasBoundary) return;
    setLoading(true);
    setError('');
    try {
      const result = await fetchGridPreview(field.id, {
        tree_count: expectedCount,
        row_spacing_m: rowSpacing,
        tree_spacing_m: treeSpacing,
        bearing_degrees: mode === 'manual' ? bearing : undefined,
      });
      setPreviewResult({
        preview: result.preview,
        requested_count: result.requested_count,
        existing_tree_count: result.existing_tree_count,
        field_code: result.field_code,
        field_name: result.field_name,
      });
      setStep('preview');
    } catch (err: any) {
      setError(err.message || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  }, [field.id, expectedCount, rowSpacing, treeSpacing, bearing, mode, hasBoundary]);

  const handleGenerate = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await generateTrees(field.id, {
        tree_count: expectedCount,
        crop_type: field.crop_type,
        row_spacing_m: rowSpacing,
        tree_spacing_m: treeSpacing,
        bearing_degrees: mode === 'manual' ? bearing : undefined,
        assigned_agronomist_id: field.assigned_agronomist_id,
      });
      setGenerateResult({
        created_count: result.created_count,
        requested_count: result.requested_count,
        mapped_count: result.mapped_count,
        inside_boundary_count: result.inside_boundary_count,
        invalid_count: result.invalid_count,
        total_after: result.total_after,
        existing_before: result.existing_before,
        grid: result.grid,
        validation: result.validation,
      });
      setStep('result');
    } catch (err: any) {
      // A reverse proxy can lose the response after PostgreSQL committed the
      // grid. Re-read authoritative status before presenting a failure or
      // encouraging a duplicate retry.
      try {
        const status = await fetchGridStatus(field.id);
        if (
          status.created >= expectedCount
          && status.mapped >= expectedCount
          && status.invalid === 0
        ) {
          const existingBefore = previewResult?.existing_tree_count || 0;
          setGenerateResult({
            created_count: Math.max(status.created - existingBefore, 0),
            requested_count: expectedCount,
            mapped_count: status.mapped,
            inside_boundary_count: status.inside_boundary,
            invalid_count: status.invalid,
            total_after: status.total_after,
            existing_before: existingBefore,
            grid: previewResult?.preview.grid || {},
            validation: {
              boundary_valid: status.has_boundary,
              all_inside_boundary: status.inside_boundary === status.mapped,
              codes_unique: true,
              count_complete: status.created >= expectedCount,
            },
          });
          setStep('result');
          return;
        }
      } catch {
        // Preserve the original generation error when status recovery fails.
      }
      setError(err.message || 'Failed to generate trees');
    } finally {
      setLoading(false);
    }
  }, [field, expectedCount, rowSpacing, treeSpacing, bearing, mode]);

  const handleClearField = useCallback(async () => {
    setClearing(true);
    setError('');
    try {
      if (!clearInfo) {
        // First call: get confirmation info
        const result = await clearFieldTrees(field.id, false);
        if (result.requires_confirmation) {
          setClearInfo(result);
        } else {
          onComplete();
          onClose();
        }
      } else {
        // Second call: confirm with typed field code
        if (confirmFieldCode.trim() !== clearInfo.field_code) {
          setError('Field code does not match. Type the exact field code to confirm.');
          return;
        }
        await clearFieldTrees(field.id, confirmFieldCode.trim());
        setClearInfo(null);
        setConfirmFieldCode('');
        onComplete();
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to clear field trees');
    } finally {
      setClearing(false);
    }
  }, [field.id, clearInfo, confirmFieldCode, onComplete, onClose]);

  const goNext = () => {
    setError('');
    if (step === 'field' && hasBoundary) {
      setStep('configure');
    } else if (step === 'configure') {
      loadPreview();
    } else if (step === 'preview') {
      setStep('confirm');
    } else if (step === 'confirm') {
      handleGenerate();
    }
  };

  const newTreeCount = previewResult
    ? Math.max((previewResult.preview.validation.proposed || 0) - previewResult.existing_tree_count, 0)
    : 0;
  const hasTreesToCreate = newTreeCount > 0;

  const canProceed = () => {
    if (step === 'field') return hasBoundary;
    if (step === 'configure') return expectedCount > 0 && expectedCount <= 5000;
    if (step === 'preview') {
      const v = previewResult?.preview.validation;
      if (!v) return false;
      return (
        v.boundary_valid
        && v.all_inside_boundary
        && v.count_complete
        && v.duplicate_positions === 0
        && v.proposed === v.inside_boundary
      );
    }
    if (step === 'confirm') return !loading && hasTreesToCreate;
    return false;
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30">
      <div className="w-full max-w-xl bg-white h-full shadow-2xl flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[11px] uppercase font-bold text-emerald-600">Tree Grid Builder</p>
              <h2 className="text-lg font-black text-slate-900">{field.name || field.plot_code}</h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex gap-1">
            {steps.map((s, idx) => (
              <div
                key={s.key}
                className={`flex-1 h-1 rounded-full transition-colors ${
                  idx < currentStepIdx
                    ? 'bg-emerald-500'
                    : idx === currentStepIdx
                    ? 'bg-emerald-600'
                    : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
          <div className="flex justify-between mt-2">
            {steps.map((s) => (
              <span
                key={s.key}
                className={`text-[10px] font-bold ${
                  s.key === step ? 'text-emerald-700' : 'text-slate-400'
                }`}
              >
                {s.label}
              </span>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-3 bg-red-50 border border-red-100 text-red-700 px-3 py-2 rounded-lg text-xs">
            {error}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {step === 'field' && (
            <div className="space-y-3">
              <FieldStep field={field} boundary={boundary} />
              {(Number(field.registered_tree_count) > 0) && !clearInfo && (
                <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
                  <p className="text-xs text-amber-700 font-semibold mb-2">
                    This field has {fmt(field.registered_tree_count)} existing trees. Trees with observations, care events, or inspections are protected.
                  </p>
                  <button
                    type="button"
                    disabled={clearing}
                    onClick={handleClearField}
                    className="flex items-center gap-1.5 rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs font-bold text-amber-700 hover:bg-amber-100 disabled:opacity-50 transition"
                  >
                    {clearing ? (
                      <div className="h-3.5 w-3.5 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <TrashIcon className="w-3.5 h-3.5" />
                    )}
                    Remove unprotected trees
                  </button>
                </div>
              )}
              {clearInfo && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-xs text-red-800 font-semibold mb-1">{clearInfo.message}</p>
                  <div className="grid grid-cols-3 gap-2 my-2">
                    <div className="text-center">
                      <p className="text-sm font-black text-slate-900">{fmt(clearInfo.deletable_count)}</p>
                      <p className="text-[10px] text-slate-500">Removable</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-black text-amber-600">{fmt(clearInfo.protected_count)}</p>
                      <p className="text-[10px] text-slate-500">Protected</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-black text-slate-900">{fmt(clearInfo.total_trees)}</p>
                      <p className="text-[10px] text-slate-500">Total</p>
                    </div>
                  </div>
                  <label className="block mb-2">
                    <span className="text-[11px] font-bold text-red-700">Type field code to confirm:</span>
                    <input
                      type="text"
                      value={confirmFieldCode}
                      onChange={(e) => setConfirmFieldCode(e.target.value)}
                      placeholder={clearInfo.field_code}
                      className="mt-1 w-full rounded-lg border border-red-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20"
                    />
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={clearing || confirmFieldCode.trim() !== clearInfo.field_code}
                      onClick={handleClearField}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-red-600 text-white px-3 py-2 text-xs font-bold hover:bg-red-700 disabled:opacity-50 transition"
                    >
                      {clearing ? (
                        <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <TrashIcon className="w-3.5 h-3.5" />
                      )}
                      Remove trees
                    </button>
                    <button
                      type="button"
                      onClick={() => { setClearInfo(null); setConfirmFieldCode(''); setError(''); }}
                      className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {step === 'configure' && (
            <ConfigureStep
              expectedCount={expectedCount}
              setExpectedCount={setExpectedCount}
              rowSpacing={rowSpacing}
              setRowSpacing={setRowSpacing}
              treeSpacing={treeSpacing}
              setTreeSpacing={setTreeSpacing}
              bearing={bearing}
              setBearing={setBearing}
              mode={mode}
              setMode={setMode}
              field={field}
            />
          )}
          {step === 'preview' && previewResult && (
            <PreviewStep preview={previewResult} boundary={boundary} />
          )}
          {step === 'confirm' && previewResult && (
            <ConfirmStep field={field} preview={previewResult} loading={loading} />
          )}
          {step === 'result' && generateResult && (
            <ResultStep
              result={generateResult}
              field={field}
              onClose={() => {
                onComplete();
                onClose();
              }}
              onViewMap={() => {
                onComplete();
                onClose();
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 shrink-0 flex gap-2">
          {currentStepIdx > 0 && step !== 'result' && (
            <button
              onClick={() => {
                setError('');
                const prev = steps[currentStepIdx - 1];
                setStep(prev.key);
              }}
              className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 flex items-center gap-1"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </button>
          )}
          {step !== 'result' && (
            <button
              onClick={goNext}
              disabled={!canProceed() || loading}
              className="flex-1 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 transition"
            >
              {loading ? (
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : step === 'confirm' ? (
                <>
                  <CheckCircleIcon className="w-4 h-4" />
                  Add {fmt(newTreeCount)} trees
                </>
              ) : step === 'preview' ? (
                <>
                  Confirm
                  <ArrowRightIcon className="w-4 h-4" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRightIcon className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TreeGridBuilderDrawer;
